// src/components/eventos/EventoRelatorio.js
import React from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton } from '@chakra-ui/react';

// ─── helpers ────────────────────────────────────────────────────────────────
const formatarData = (s) => {
    if (!s) return 'Data não informada';
    try { const [a, m, d] = s.split('T')[0].split('-'); return `${d}/${m}/${a}`; } catch { return 'Data inválida'; }
};
const formatarDataCompleta = (s) => {
    if (!s) return 'Data não informada';
    try {
        const [a, m, d] = s.split('T')[0].split('-').map(Number);
        const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
        const dias = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
        const dt = new Date(a, m - 1, d);
        return `${dias[dt.getDay()]}, ${d} de ${meses[m - 1]} de ${a}`;
    } catch { return formatarData(s); }
};
const formatarTempo = (min) => {
    const h = Math.floor(Math.abs(min) / 60), r = Math.abs(min) % 60, s = min < 0 ? '-' : '';
    return h > 0 ? `${s}${h}h ${r}min` : `${s}${r}min`;
};
const horaMin = (str) => {
    if (!str || !str.includes(':')) return null;
    const [h, m] = str.split(':').map(Number);
    return h * 60 + m;
};
const calcDur = (a, b) => {
    const ia = horaMin(a), ib = horaMin(b);
    if (ia === null || ib === null) return null;
    let d = ib - ia; if (d < 0) d += 1440; return d;
};

// ─── icons ──────────────────────────────────────────────────────────────────
const PrintIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
        <rect x="6" y="14" width="12" height="8"/>
    </svg>
);
const CheckSm = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const WarnSm = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

// ─── stat card ───────────────────────────────────────────────────────────────
const Stat = ({ label, value, sub, color = '#1e293b' }) => (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
        <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
        <p style={{ margin: '0 0 2px', fontSize: '22px', fontWeight: 800, color, letterSpacing: '-0.02em' }}>{value}</p>
        {sub && <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{sub}</p>}
    </div>
);

// ─── section wrapper ─────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px' }}>
        <p style={{ margin: '0 0 16px', fontSize: '13px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</p>
        {children}
    </div>
);

// ────────────────────────────────────────────────────────────────────────────
const EventoRelatorio = ({ isOpen, onClose, evento }) => {
    if (!evento) return null;

    // ── calcular métricas ──
    const tarefas = evento.tarefas || [];
    const total = tarefas.length;
    const concluidas = tarefas.filter(t => t.status === 'concluida').length;
    const pctConclusao = total > 0 ? (concluidas / total) * 100 : 0;
    let tempoProg = 0, tempoReal = 0, noPrazo = 0, atrasadas = 0;

    tarefas.forEach(t => {
        tempoProg += t.duracao || 0;
        if (t.horaInicioReal && t.horaFimReal) {
            const dur = calcDur(t.horaInicioReal, t.horaFimReal);
            if (dur !== null) {
                tempoReal += dur;
                dur <= (t.duracao || 0) ? noPrazo++ : atrasadas++;
            }
        }
    });

    const difTempo = tempoReal - tempoProg;
    const pctTempo = tempoProg > 0 ? ((tempoReal / tempoProg) * 100) - 100 : 0;
    const duracaoEvento = calcDur(evento.hora_inicio_real, evento.hora_fim_real) ?? 0;
    const pctNoPrazo = total > 0 ? Math.round((noPrazo / total) * 100) : 0;
    const pctAtrasadas = total > 0 ? Math.round((atrasadas / total) * 100) : 0;

    const perfColor = pctNoPrazo >= 90 ? '#2CCB90' : pctNoPrazo >= 70 ? '#d97706' : '#ef4444';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay bg="rgba(0,0,0,0.4)" backdropFilter="blur(8px)" />
            <ModalContent bg="#ffffff" border="1px solid #e2e8f0" borderRadius="20px" color="#1e293b" mx={4} fontFamily="'Inter', -apple-system, sans-serif" boxShadow="0 20px 60px rgba(0,0,0,0.15)">

                <ModalHeader pb={3} borderBottom="1px solid #e2e8f0">
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '100px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '11px', fontWeight: 700, marginBottom: '8px' }}>
                        RELATÓRIO DO EVENTO
                    </span>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{evento.titulo}</p>
                </ModalHeader>
                <ModalCloseButton color="#94a3b8" />

                <ModalBody py={6} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* Informações Gerais */}
                    <Section title="Informações Gerais">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                            {[
                                { label: 'Data', value: formatarDataCompleta(evento.data) },
                                { label: 'Horário', value: `${evento.hora_inicio_real || '--:--'} às ${evento.hora_fim_real || '--:--'}` },
                                { label: 'Duração Total', value: formatarTempo(duracaoEvento) },
                                { label: 'Status', value: 'Concluído', color: '#2CCB90' },
                                ...(evento.local ? [{ label: 'Local', value: evento.local }] : []),
                            ].map((item, i) => (
                                <div key={i}>
                                    <p style={{ margin: '0 0 3px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</p>
                                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: item.color || '#1e293b' }}>{item.value}</p>
                                </div>
                            ))}
                        </div>
                        {evento.descricao && (
                            <div style={{ marginTop: '14px', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Descrição</p>
                                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{evento.descricao}</p>
                            </div>
                        )}
                    </Section>

                    {/* Métricas de Desempenho */}
                    <Section title="Métricas de Desempenho">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                            <Stat label="Tarefas Concluídas" value={`${concluidas}/${total}`} sub={`${Math.round(pctConclusao)}% completo`} color="#1e293b" />
                            <Stat label="No Prazo" value={noPrazo} sub={`${pctNoPrazo}% do total`} color={perfColor} />
                            <Stat label="Atrasadas" value={atrasadas} sub={`${pctAtrasadas}% do total`} color={atrasadas > 0 ? '#ef4444' : '#2CCB90'} />
                        </div>
                        {/* Barra de progresso */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', color: '#94a3b8' }}>
                                <span>Progresso Geral</span>
                                <span style={{ fontWeight: 700, color: perfColor }}>{Math.round(pctConclusao)}%</span>
                            </div>
                            <div style={{ height: '10px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pctConclusao}%`, borderRadius: '100px', background: `linear-gradient(90deg, ${perfColor}, ${perfColor}cc)`, transition: 'width 0.6s ease' }} />
                            </div>
                        </div>
                    </Section>

                    {/* Análise de Tempo */}
                    <Section title="Análise de Tempo">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                            <Stat label="Tempo Programado" value={formatarTempo(tempoProg)} sub="Total planejado" />
                            <Stat label="Tempo Real" value={formatarTempo(tempoReal)} sub={`${difTempo > 0 ? '+' : ''}${Math.round(pctTempo)}% do planejado`} color={difTempo <= 0 ? '#2CCB90' : '#ef4444'} />
                            <Stat label="Diferença" value={`${difTempo > 0 ? '+' : ''}${formatarTempo(difTempo)}`} sub={difTempo > 0 ? 'acima do planejado' : difTempo < 0 ? 'abaixo do planejado' : 'exato'} color={difTempo <= 0 ? '#2CCB90' : '#ef4444'} />
                        </div>
                        <div style={{ padding: '12px 14px', borderRadius: '10px', background: difTempo <= 0 ? 'rgba(44,203,144,0.06)' : '#fffbeb', border: `1px solid ${difTempo <= 0 ? 'rgba(44,203,144,0.2)' : '#fcd34d'}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <span style={{ color: difTempo <= 0 ? '#2CCB90' : '#d97706', marginTop: '1px', flexShrink: 0 }}>
                                {difTempo <= 0 ? <CheckSm /> : <WarnSm />}
                            </span>
                            <div>
                                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 600, color: difTempo <= 0 ? '#2CCB90' : '#d97706' }}>
                                    {difTempo <= 0 ? 'Evento concluído dentro do prazo!' : 'Evento ultrapassou o tempo previsto'}
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                                    Diferença de {formatarTempo(Math.abs(difTempo))} {difTempo > 0 ? 'a mais' : 'a menos'} que o planejado
                                </p>
                            </div>
                        </div>
                    </Section>

                    {/* Detalhamento de Tarefas */}
                    <Section title={`Detalhamento de Tarefas (${total})`}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {tarefas.map((tarefa, idx) => {
                                const tr = (() => {
                                    if (!tarefa.horaInicioReal || !tarefa.horaFimReal) return 0;
                                    return calcDur(tarefa.horaInicioReal, tarefa.horaFimReal) ?? 0;
                                })();
                                const dif = tr > 0 ? tr - (tarefa.duracao || 0) : null;
                                const ok = dif !== null ? dif <= 0 : null;

                                return (
                                    <div key={tarefa.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', transition: 'border-color 0.2s' }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)'}
                                        onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                    >
                                        {/* Header */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                            <span style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '11px', fontWeight: 700 }}>
                                                #{idx + 1}
                                            </span>
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', flex: 1 }}>{tarefa.titulo}</span>
                                            {ok !== null && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: ok ? 'rgba(44,203,144,0.1)' : '#fffbeb', border: `1px solid ${ok ? 'rgba(44,203,144,0.2)' : '#fcd34d'}`, color: ok ? '#2CCB90' : '#d97706', fontSize: '11px', fontWeight: 600 }}>
                                                    {ok ? <CheckSm /> : <WarnSm />}
                                                    {ok ? 'No prazo' : 'Atrasou'}
                                                </span>
                                            )}
                                        </div>

                                        {/* Grid de tempos */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                                            <div>
                                                <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Programado</p>
                                                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{tarefa.horaInicio} – {tarefa.horaFim}</p>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{tarefa.duracao} min</p>
                                            </div>
                                            <div>
                                                <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Realizado</p>
                                                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                    {tarefa.horaInicioReal || '--:--'} – {tarefa.horaFimReal || '--:--'}
                                                </p>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{tr > 0 ? `${tr} min` : '--'}</p>
                                            </div>
                                            <div>
                                                <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Diferença</p>
                                                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 700, color: dif === null ? '#94a3b8' : dif <= 0 ? '#2CCB90' : '#ef4444' }}>
                                                    {dif === null ? '—' : dif === 0 ? 'Exato' : `${dif > 0 ? '+' : ''}${dif} min`}
                                                </p>
                                                {tarefa.responsavel && <p style={{ margin: '0 0 1px', fontSize: '11px', color: '#94a3b8' }}>• {tarefa.responsavel}</p>}
                                                {tarefa.local && <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>📍 {tarefa.local}</p>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Section>

                    {/* Resumo final */}
                    <div style={{ padding: '16px 18px', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: '12px', fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resumo</p>
                        {pctConclusao === 100
                            ? 'Todas as tarefas foram concluídas com sucesso. '
                            : `${concluidas} de ${total} tarefas concluídas. `
                        }
                        {atrasadas === 0 ? 'Nenhuma tarefa atrasou.' : `${atrasadas} tarefa(s) ultrapassaram o tempo previsto.`}
                        {' '}O evento teve duração total de {formatarTempo(duracaoEvento)}
                        {difTempo > 0
                            ? `, com ${formatarTempo(difTempo)} a mais que o planejado.`
                            : difTempo < 0
                                ? `, finalizando ${formatarTempo(Math.abs(difTempo))} antes do previsto.`
                                : ', exatamente conforme o planejado.'
                        }
                    </div>
                </ModalBody>

                <ModalFooter borderTop="1px solid #e2e8f0" gap={3}>
                    <button onClick={() => window.print()}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 18px', borderRadius: '10px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                        <PrintIcon /> Imprimir / PDF
                    </button>
                    <button onClick={onClose}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', borderRadius: '10px', background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}>
                        Fechar Relatório
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EventoRelatorio;
